import React from "react";
import {
  Box,
  Image,
  Button,
  Stack,
  Text,
  Container,
  SimpleGrid,
  Icon,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  Heading,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import HeroSection from "../components/herosection/HeroSection";
import FeaturedSellers from "../components/featuredSellers/featuredSellers";
import FeaturedCars from "../components/featuredCars/featuredCars";
import FeaturedBikes from "../components/featuredBikes/featuredBikes";
import SellCarSection from "../components/sellCarSection/SellCarSection";
import AboutUsSection from "../components/aboutUs/AboutUs";
import AvailableCarBrands from "../components/carBrands/CarBrands";
import AnimatedGradientLine from "../components/animatedLine/AnimatedLine";
import SellImage from "../images/featured.jpg";
import CabImage from "../images/book.jpg";
import FeaturedSellerImage from "../images/featured_seller.jpg";
import BeFeaturedSeller from "../components/beFeaturedSeller/beFeaturedSeller";
import saleImage from "../images/sale.jpg";
import rentImage from "../images/rent.jpg";
import rideImage from "../images/ride.jpg";
import {
  FiShoppingBag,
  FiUsers,
  FiCalendar,
  FiMapPin,
  FiClock,
  FiDollarSign,
  FiShield,
  FiTrendingUp,
  FiHeart,
} from "react-icons/fi";
import {
  FaCar,
  FaHandshake,
  FaStar,
  FaShieldAlt,
  FaHeadset,
  FaRocket,
} from "react-icons/fa";

const MotionBox = motion(Box);
const MotionStack = motion(Stack);

const Home = () => {
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");
  const statsBg = useColorModeValue("gray.50", "gray.900");
  const serviceBg = useColorModeValue("gray.50", "gray.900");
  const featureBg = useColorModeValue("gray.50", "gray.700");

  const features = [
    {
      icon: FaRocket,
      title: "Fast & Easy Listing",
      description:
        "List your car in minutes with our simple and intuitive interface",
      color: "blue",
    },
    {
      icon: FaShieldAlt,
      title: "Secure Transactions",
      description: "Safe and verified payments with buyer & seller protection",
      color: "green",
    },
    {
      icon: FaHeadset,
      title: "24/7 Support",
      description: "Dedicated customer support team always ready to help",
      color: "purple",
    },
    {
      icon: FaStar,
      title: "Verified Sellers",
      description: "All sellers are verified for your peace of mind",
      color: "orange",
    },
  ];

  const stats = [
    { value: "10K+", label: "Active Listings", icon: FiShoppingBag },
    { value: "5K+", label: "Happy Customers", icon: FiUsers },
    { value: "98%", label: "Satisfaction Rate", icon: FiHeart },
    { value: "24/7", label: "Customer Support", icon: FiClock },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <>
      <HeroSection />

      {/* Stats Section */}
      <Box py={16} bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <Container maxW="1200px" mx="auto" px={4}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
            {stats.map((stat, index) => (
              <MotionBox
                key={index}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  delay: index * 0.1,
                  type: "spring",
                  stiffness: 200,
                }}
                textAlign="center"
                color="white"
              >
                <Icon as={stat.icon} w={10} h={10} mb={3} opacity={0.9} />
                <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold">
                  {stat.value}
                </Text>
                <Text fontSize="sm" opacity={0.9}>
                  {stat.label}
                </Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <SellCarSection />
      <AnimatedGradientLine />
      <FeaturedSellers />
      <AnimatedGradientLine />

      <Box py={12}>
        <FeaturedCars />
      </Box>

      <AnimatedGradientLine />
      <FeaturedBikes />
      <AnimatedGradientLine />
      <BeFeaturedSeller />

      {/* Explore Section */}
      <Box py={16} bg={statsBg}>
        <Container maxW="1200px" mx="auto" px={4}>
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            textAlign="center"
            mb={12}
          >
            <Badge
              colorScheme="blue"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
              mb={4}
            >
              Explore Our Services
            </Badge>
            <Heading
              as="h2"
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="bold"
              bgGradient="linear(to-r, blue.600, purple.600)"
              bgClip="text"
              mb={4}
            >
              Find Your Perfect Ride
            </Heading>
            <Text fontSize="lg" color={textColor} maxW="600px" mx="auto">
              Choose from thousands of cars for sale, rent, or share rides with
              our community
            </Text>
          </MotionBox>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {/* Cars For Sale Card */}
            <MotionBox
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
              transition={{ duration: 0.3 }}
              bg={cardBg}
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="xl"
              position="relative"
            >
              <Box position="relative" h="260px" overflow="hidden">
                <Image
                  src={saleImage}
                  alt="Cars for sale"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  transition="transform 0.5s"
                  _hover={{ transform: "scale(1.1)" }}
                />
                <Box
                  position="absolute"
                  top={4}
                  right={4}
                  bg="blue.500"
                  color="white"
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="sm"
                  fontWeight="bold"
                >
                  Popular
                </Box>
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  bg="linear-gradient(to top, rgba(0,0,0,0.8), transparent)"
                  p={6}
                >
                  <Text color="white" fontSize="lg" fontWeight="bold">
                    Best Deals Available
                  </Text>
                </Box>
              </Box>

              <Box p={6}>
                <HStack mb={3} spacing={2}>
                  <Icon as={FiShoppingBag} color="blue.500" />
                  <Text fontSize="sm" color="blue.500" fontWeight="semibold">
                    For Sale
                  </Text>
                </HStack>
                <Heading as="h3" size="lg" mb={3} color={headingColor}>
                  Cars For Sale
                </Heading>
                <Text color={textColor} mb={4} lineHeight="tall">
                  Discover thousands of new and used cars from verified sellers.
                  Best prices, certified vehicles, and easy financing options
                  available.
                </Text>
                <VStack align="start" spacing={2} mb={6}>
                  <HStack>
                    <Icon as={FaCar} color="green.500" />
                    <Text fontSize="sm" color={textColor}>
                      1000+ Cars Available
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiDollarSign} color="green.500" />
                    <Text fontSize="sm" color={textColor}>
                      Best Price Guarantee
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiShield} color="green.500" />
                    <Text fontSize="sm" color={textColor}>
                      Verified Sellers Only
                    </Text>
                  </HStack>
                </VStack>
                <Link to="/category/sale">
                  <Button
                    w="full"
                    size="lg"
                    colorScheme="blue"
                    rightIcon={<Icon as={FiTrendingUp} />}
                    _hover={{
                      transform: "translateX(5px)",
                      boxShadow: "lg",
                    }}
                    transition="all 0.3s"
                  >
                    Browse Cars for Sale
                  </Button>
                </Link>
              </Box>
            </MotionBox>

            {/* Ride Sharing / Carpool Card */}
            <MotionBox
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
              transition={{ duration: 0.3 }}
              bg={cardBg}
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="xl"
              position="relative"
            >
              <Box position="relative" h="260px" overflow="hidden">
                <Image
                  src={rideImage}
                  alt="Ride sharing"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  transition="transform 0.5s"
                  _hover={{ transform: "scale(1.1)" }}
                />
                <Box
                  position="absolute"
                  top={4}
                  right={4}
                  bg="purple.500"
                  color="white"
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="sm"
                  fontWeight="bold"
                >
                  Eco-Friendly
                </Box>
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  bg="linear-gradient(to top, rgba(0,0,0,0.8), transparent)"
                  p={6}
                >
                  <Text color="white" fontSize="lg" fontWeight="bold">
                    Save Money, Travel Together
                  </Text>
                </Box>
              </Box>

              <Box p={6}>
                <HStack mb={3} spacing={2}>
                  <Icon as={FiUsers} color="purple.500" />
                  <Text fontSize="sm" color="purple.500" fontWeight="semibold">
                    Carpool & Ride Sharing
                  </Text>
                </HStack>
                <Heading as="h3" size="lg" mb={3} color={headingColor}>
                  Book A Ride / Carpool
                </Heading>
                <Text color={textColor} mb={4} lineHeight="tall">
                  Share rides with fellow travelers heading your way. Reduce
                  costs, make new friends, and help the environment by sharing a
                  ride.
                </Text>
                <VStack align="start" spacing={2} mb={6}>
                  <HStack>
                    <Icon as={FiMapPin} color="purple.500" />
                    <Text fontSize="sm" color={textColor}>
                      Multiple Cities Across Pakistan
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiCalendar} color="purple.500" />
                    <Text fontSize="sm" color={textColor}>
                      Flexible Departure Dates
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiDollarSign} color="purple.500" />
                    <Text fontSize="sm" color={textColor}>
                      Save Up to 70% vs Solo Travel
                    </Text>
                  </HStack>
                </VStack>
                <Link to="/category/ride">
                  <Button
                    w="full"
                    size="lg"
                    colorScheme="purple"
                    rightIcon={<Icon as={FiUsers} />}
                    _hover={{
                      transform: "translateX(5px)",
                      boxShadow: "lg",
                    }}
                    transition="all 0.3s"
                  >
                    Find Rides & Carpool
                  </Button>
                </Link>
              </Box>
            </MotionBox>

            {/* Rent A Car Card */}
            <MotionBox
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
              transition={{ duration: 0.3 }}
              bg={cardBg}
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="xl"
              position="relative"
            >
              <Box position="relative" h="260px" overflow="hidden">
                <Image
                  src={rentImage}
                  alt="Rent a car"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  transition="transform 0.5s"
                  _hover={{ transform: "scale(1.1)" }}
                />
                <Box
                  position="absolute"
                  top={4}
                  right={4}
                  bg="green.500"
                  color="white"
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="sm"
                  fontWeight="bold"
                >
                  Flexible Terms
                </Box>
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  bg="linear-gradient(to top, rgba(0,0,0,0.8), transparent)"
                  p={6}
                >
                  <Text color="white" fontSize="lg" fontWeight="bold">
                    Drive Your Dream Car
                  </Text>
                </Box>
              </Box>

              <Box p={6}>
                <HStack mb={3} spacing={2}>
                  <Icon as={FaCar} color="green.500" />
                  <Text fontSize="sm" color="green.500" fontWeight="semibold">
                    For Rent
                  </Text>
                </HStack>
                <Heading as="h3" size="lg" mb={3} color={headingColor}>
                  Rent A Car
                </Heading>
                <Text color={textColor} mb={4} lineHeight="tall">
                  Rent premium cars for daily, weekly, or monthly needs. Wide
                  selection, competitive rates, and doorstep delivery available.
                </Text>
                <VStack align="start" spacing={2} mb={6}>
                  <HStack>
                    <Icon as={FiClock} color="green.500" />
                    <Text fontSize="sm" color={textColor}>
                      Daily/Weekly/Monthly Rentals
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaShieldAlt} color="green.500" />
                    <Text fontSize="sm" color={textColor}>
                      Full Insurance Coverage
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiMapPin} color="green.500" />
                    <Text fontSize="sm" color={textColor}>
                      Free Delivery & Pickup
                    </Text>
                  </HStack>
                </VStack>
                <Link to="/category/rent">
                  <Button
                    w="full"
                    size="lg"
                    colorScheme="green"
                    rightIcon={<Icon as={FaCar} />}
                    _hover={{
                      transform: "translateX(5px)",
                      boxShadow: "lg",
                    }}
                    transition="all 0.3s"
                  >
                    Rent a Car Now
                  </Button>
                </Link>
              </Box>
            </MotionBox>
          </SimpleGrid>
        </Container>
      </Box>

      <AnimatedGradientLine />

      {/* Features Section */}
      <Box py={20} bg={cardBg}>
        <Container maxW="1200px" mx="auto" px={4}>
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            textAlign="center"
            mb={12}
          >
            <Badge
              colorScheme="blue"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
              mb={4}
            >
              Why Choose Us
            </Badge>
            <Heading
              as="h2"
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="bold"
              bgGradient="linear(to-r, blue.600, purple.600)"
              bgClip="text"
              mb={4}
            >
              The NCPWheels Advantage
            </Heading>
            <Text fontSize="lg" color={textColor} maxW="600px" mx="auto">
              We provide the best platform for buying, selling, and sharing
              vehicles
            </Text>
          </MotionBox>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
            {features.map((feature, index) => (
              <MotionBox
                key={index}
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
                textAlign="center"
                p={6}
                borderRadius="xl"
                bg={featureBg}
              >
                <Box
                  w={16}
                  h={16}
                  mx="auto"
                  mb={4}
                  borderRadius="full"
                  bg={`${feature.color}.100`}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon
                    as={feature.icon}
                    w={8}
                    h={8}
                    color={`${feature.color}.500`}
                  />
                </Box>
                <Heading as="h3" size="md" mb={3} color={headingColor}>
                  {feature.title}
                </Heading>
                <Text color={textColor} fontSize="sm">
                  {feature.description}
                </Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>
      <AboutUsSection />

      {/* Services Section */}
      <Box py={20} bg={serviceBg}>
        <Container maxW="1200px" mx="auto" px={4}>
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            textAlign="center"
            mb={12}
          >
            <Badge
              colorScheme="blue"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
              mb={4}
            >
              Our Premium Services
            </Badge>
            <Heading
              as="h2"
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="bold"
              bgGradient="linear(to-r, blue.600, purple.600)"
              bgClip="text"
              mb={4}
            >
              Everything You Need Under One Roof
            </Heading>
            <Text fontSize="lg" color={textColor} maxW="600px" mx="auto">
              Comprehensive solutions for all your vehicle needs
            </Text>
          </MotionBox>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {[
              {
                img: FeaturedSellerImage,
                title: "Seller Listings Management",
                description:
                  "Be our Featured Seller and have our back in managing everything for you from creating your store, uploading and managing your listings to handling customers for you.",
                icon: FiShoppingBag,
                color: "blue",
              },
              {
                img: SellImage,
                title: "Sell Your Car with Us",
                description:
                  "Have our back to list your car on our front page and on top of other cars listings. This will help customers to find you easily and help you sell your car within days of listing.",
                icon: FaHandshake,
                color: "green",
              },
              {
                img: CabImage,
                title: "List Your Cab for Rides",
                description:
                  "Enhance your business in creating more opportunities to find more customers by using NCPWheels. Help yourself and your customers to find each other more easily.",
                icon: FiUsers,
                color: "purple",
              },
            ].map((service, index) => (
              <MotionBox
                key={index}
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
                bg={cardBg}
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="lg"
              >
                <Box position="relative" h="250px" overflow="hidden">
                  <Image
                    src={service.img}
                    alt={service.title}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    transition="transform 0.5s"
                    _hover={{ transform: "scale(1.1)" }}
                  />
                  <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    bg="linear-gradient(to top, rgba(0,0,0,0.7), transparent)"
                    p={4}
                  >
                    <HStack spacing={2}>
                      <Icon as={service.icon} color="white" w={5} h={5} />
                      <Text color="white" fontWeight="bold">
                        {service.title}
                      </Text>
                    </HStack>
                  </Box>
                </Box>
                <Box p={6}>
                  <Text color={textColor} lineHeight="tall">
                    {service.description}
                  </Text>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <AvailableCarBrands />

      {/* CTA Section */}
      <Box py={16} bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <Container maxW="800px" mx="auto" textAlign="center" px={4}>
          <MotionBox
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Icon
              as={FaRocket}
              w={16}
              h={16}
              color="white"
              mb={6}
              opacity={0.9}
            />
            <Heading
              as="h2"
              fontSize={{ base: "3xl", md: "4xl" }}
              color="white"
              mb={4}
            >
              Ready to Get Started?
            </Heading>
            <Text
              fontSize="lg"
              color="white"
              opacity={0.9}
              mb={8}
              maxW="600px"
              mx="auto"
            >
              Join thousands of happy customers who found their perfect ride
              through NCPWheels
            </Text>
            <Link to="/category/sale">
              <Button
                size="lg"
                bg="white"
                color="purple.600"
                _hover={{
                  transform: "scale(1.05)",
                  boxShadow: "2xl",
                }}
                transition="all 0.3s"
                rightIcon={<Icon as={FiTrendingUp} />}
              >
                Start Your Journey Today
              </Button>
            </Link>
          </MotionBox>
        </Container>
      </Box>
    </>
  );
};

export default Home;
